import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, Plus, Home, BookOpen, User, Settings } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { User as SupabaseUser } from '@supabase/supabase-js';

interface Booking {
  id: string;
  created_at: string;
  service_date: string;
  service_type: string;
  status: string;
  description: string;
}

export function CustomerPortal() {
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('bookings');
  const navigate = useNavigate();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleNewBooking = () => {
    navigate('/#contact');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-mint-600">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  const menuItems = [
    { icon: Home, label: 'Dashboard', id: 'dashboard' },
    { icon: BookOpen, label: 'Bookings', id: 'bookings' },
    { icon: User, label: 'Profile', id: 'profile' },
    { icon: Settings, label: 'Settings', id: 'settings' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-20 flex">
      {/* Side Menu */}
      <div className="w-64 bg-white shadow-lg fixed h-[calc(100vh-5rem)] top-20">
        <div className="p-4">
          <div className="space-y-2">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  activeSection === item.id
                    ? 'bg-mint-50 text-mint-600'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 ml-64 p-8">
        <div className="max-w-4xl">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              {menuItems.find(item => item.id === activeSection)?.label}
            </h1>
            <button
              onClick={() => supabase.auth.signOut()}
              className="text-gray-600 hover:text-gray-800 transition-colors"
            >
              Sign Out
            </button>
          </div>

          {activeSection === 'bookings' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Your Bookings</h2>
                <button
                  onClick={handleNewBooking}
                  className="bg-mint-600 hover:bg-mint-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  New Booking
                </button>
              </div>

              <div className="space-y-4">
                <div className="border border-gray-200 rounded-lg p-4 hover:border-mint-500 transition-colors">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-semibold text-gray-900">Computer Repair</h3>
                    <span className="bg-mint-100 text-mint-800 text-sm px-3 py-1 rounded-full">Scheduled</span>
                  </div>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>March 15, 2024</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>2:00 PM</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>Home Visit</span>
                    </div>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4 hover:border-mint-500 transition-colors">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-semibold text-gray-900">Network Setup</h3>
                    <span className="bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full">Completed</span>
                  </div>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>March 10, 2024</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>10:00 AM</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>Home Visit</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'profile' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Your Information</h2>
              <p className="text-gray-600">Email: {user.email}</p>
            </div>
          )}

          {activeSection === 'dashboard' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Welcome to your dashboard</h2>
              <p className="text-gray-600">View your recent activity and manage your services.</p>
            </div>
          )}

          {activeSection === 'settings' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Account Settings</h2>
              <p className="text-gray-600">Manage your account preferences and notifications.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}